/**
 * Dashboard hero — unified admin welcome, health pill, primary CTAs (chat + organizations).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

interface DashboardHeroProps {
  username?: string;
  enabledOrgCount: number;
  health?: {
    loading: boolean;
    error: string | null;
    status: 'ok' | 'degraded' | null;
    version?: string;
  };
}

export default function DashboardHero({ username, enabledOrgCount, health }: DashboardHeroProps) {
  const { t } = useTranslation();

  const pill = (() => {
    if (!health || health.loading) {
      return { label: t('dashboard.healthChecking', { defaultValue: 'กำลังตรวจสอบ' }), className: 'bg-app-muted/50 text-app-text-dim' };
    }
    if (health.error || !health.status) {
      return {
        label: t('dashboard.healthUnreachable', { defaultValue: 'ติดต่อไม่ได้' }),
        className: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
      };
    }
    if (health.status === 'ok') {
      return {
        label: health.version ? `API v${health.version}` : t('layout.healthOk', { defaultValue: 'ปกติ' }),
        className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
      };
    }
    return {
      label: t('layout.healthDegraded', { defaultValue: 'เสื่อม' }),
      className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    };
  })();

  const greeting = username?.trim()
    ? t('dashboard.heroGreetingNamed', { name: username, defaultValue: 'สวัสดี {{name}}' })
    : t('dashboard.heroGreeting', { defaultValue: 'ยินดีต้อนรับ' });

  return (
    <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-gradient-to-br from-app-surface via-app-muted/20 to-app-surface px-5 py-6 shadow-sm sm:px-8 sm:py-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-app-accent">
            {t('dashboard.heroEyebrow', { defaultValue: 'Agent Station' })}
          </p>
          <div>
            <p className="text-sm text-app-text-sec">{greeting}</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
              {t('dashboard.titleHome', { defaultValue: 'ศูนย์ควบคุม Agent Station' })}
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-app-text-sec">
              {t('dashboard.heroLeadHome', {
                count: enabledOrgCount,
                defaultValue:
                  'จัดการองค์กรที่เปิดใช้ แชทกับเอเจนต์ และดูสถานะระบบ — เปิดใช้แล้ว {{count}} องค์กร',
              })}
            </p>
          </div>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold',
              pill.className,
            )}
          >
            <SignalIcon className="h-3.5 w-3.5 shrink-0" />
            {pill.label}
          </span>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center shrink-0">
          <Link
            to="/organizations"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-app-border bg-app-bg px-5 py-3 text-sm font-semibold text-app-text transition-colors hover:bg-app-muted/50"
          >
            <BuildingOffice2Icon className="h-5 w-5 text-app-accent" />
            {t('dashboard.heroCtaOrgs', { defaultValue: 'องค์กร' })}
          </Link>
          <Link
            to="/chat-test"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-app-accent px-5 py-3 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            {t('dashboard.heroCtaChat', { defaultValue: 'ทดสอบแชท' })}
            <ArrowRightIcon className="h-4 w-4 opacity-80" />
          </Link>
        </div>
      </div>
    </section>
  );
}
